import React, { useState } from 'react';
import {
  ShieldCheck,
  Lock,
  User,
  Eye,
  EyeOff,
  Clock,
  Sparkles,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Sun,
  Moon
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const SESSION_KEY = 'dasara_auth_session';
export const SESSION_DURATION_MS = 45 * 60 * 1000; // 45 minutes in milliseconds

export const checkAuthSession = (): boolean => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const session = JSON.parse(raw);
    if (!session || !session.loginTime) return false;
    const elapsed = Date.now() - session.loginTime;
    if (elapsed < SESSION_DURATION_MS) {
      return true;
    }
    sessionStorage.removeItem(SESSION_KEY);
    return false;
  } catch {
    return false;
  }
};

export const clearAuthSession = () => {
  sessionStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event('auth:logout'));
};

export const saveAuthSession = (username: string) => {
  const session = {
    user: username,
    loginTime: Date.now(),
    expiresAt: Date.now() + SESSION_DURATION_MS
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

interface LoginPageProps {
  onLoginSuccess: () => void;
}

interface MapCamera {
  id: string;
  name: string;
  sector: string;
  x: number;
  y: number;
  type: 'inflow' | 'queue' | 'sanctum' | 'exit' | 'holding' | 'emergency';
}

const MAP_CAMERAS: MapCamera[] = [
  { id: 'CAM-01', name: 'Kummaripalem Feeder Toll Gate', sector: 'S-4', x: 80, y: 350, type: 'inflow' },
  { id: 'CAM-02', name: 'Vinayaka Temple Junction', sector: 'S-4', x: 130, y: 440, type: 'inflow' },
  { id: 'CAM-03', name: 'Head Water Works Inflow Hub', sector: 'S-6', x: 190, y: 280, type: 'inflow' },
  { id: 'CAM-04', name: 'Holding Complex S-22 Cell A', sector: 'S-22', x: 270, y: 140, type: 'holding' },
  { id: 'CAM-05', name: 'Holding Complex S-22 Cell B', sector: 'S-22', x: 330, y: 110, type: 'holding' },
  { id: 'CAM-06', name: 'Police Emergency Bypass Track', sector: 'SEC-EM', x: 230, y: 400, type: 'emergency' },
  { id: 'CAM-07', name: 'OM Turning Convergence Node', sector: 'S-7', x: 360, y: 240, type: 'queue' },
  { id: 'CAM-08A', name: 'S-8 Queue Lines (General 1-3)', sector: 'S-8', x: 450, y: 190, type: 'queue' },
  { id: 'CAM-08B', name: 'S-8 Special / VIP Line Channel', sector: 'S-8', x: 480, y: 235, type: 'queue' },
  { id: 'CAM-09', name: 'Old Rajagopuram Approach', sector: 'S-9', x: 560, y: 155, type: 'queue' },
  { id: 'CAM-10', name: 'Sanctum Antharalayam Cordon', sector: 'S-10', x: 650, y: 110, type: 'sanctum' },
  { id: 'CAM-11', name: 'Mouna Muni Direct Egress Ramp', sector: 'S-11', x: 740, y: 90, type: 'exit' },
  { id: 'CAM-12', name: 'Mahamandapam & Sivalayam Stairs', sector: 'S-13', x: 690, y: 230, type: 'exit' },
  { id: 'CAM-13', name: 'Annadanam & Prasadam Way', sector: 'S-16', x: 590, y: 340, type: 'exit' },
  { id: 'CAM-14', name: 'Kanaka Durga Nagar Main Exit', sector: 'S-15', x: 460, y: 420, type: 'exit' }
];

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedUser = username.trim();
    const trimmedPass = password.trim();

    if (!trimmedUser || !trimmedPass) {
      setError('Please enter both operator username and security PIN.');
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      // Username: admin, Password: 5757
      if (trimmedUser.toLowerCase() === 'admin' && trimmedPass === '5757') {
        saveAuthSession(trimmedUser);
        setIsLoading(false);
        onLoginSuccess();
      } else {
        setIsLoading(false);
        setError('Invalid credentials. Please verify Operator ID and PIN.');
      }
    }, 350);
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 dark:bg-[#0c0f17] text-slate-800 dark:text-slate-100 font-sans flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden transition-colors duration-200 selection:bg-blue-500/25 selection:text-blue-600 dark:selection:text-blue-300">
      {/* Theme Toggle Button in top corner */}
      <button
        onClick={toggleTheme}
        type="button"
        className="absolute top-4 right-4 z-50 p-2.5 rounded-full bg-white/90 dark:bg-[#151a24]/90 backdrop-blur-md border border-slate-200 dark:border-[#262e40] shadow-sm text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:scale-105 transition-all cursor-pointer"
        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label="Toggle Theme"
      >
        {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-blue-600" />}
      </button>

      {/* Ambient background glows */}
      <div className="absolute top-1/4 left-1/4 w-[850px] h-[600px] bg-gradient-to-b from-blue-500/10 via-indigo-500/5 to-transparent blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute bottom-10 right-1/4 w-[600px] h-[600px] bg-sky-500/10 dark:bg-sky-500/5 blur-[180px] rounded-full pointer-events-none" />

      {/* Outer Container (Max-Width 1440px Split-View) */}
      <div className="relative w-full max-w-[1440px] z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-stretch">
        
        {/* ================= LEFT SIDE (7 COLS): FEATURES & TACTICAL MAP ================= */}
        <div className="lg:col-span-7 flex flex-col justify-between relative min-h-[480px] sm:min-h-[540px] lg:min-h-[600px] lg:border-r lg:border-slate-200 dark:lg:border-[#1e2638] lg:pr-8 xl:pr-10">
          
          {/* Top Feature Highlights Header */}
          <div className="pb-2 mb-2 z-10">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="font-mono text-[9.5px] px-2.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30 uppercase font-bold tracking-wider">
                Integrated Operational Platform
              </span>
              <span className="text-[11px] font-mono text-slate-400 dark:text-slate-500">·</span>
              <span className="text-[11px] font-mono text-slate-600 dark:text-slate-300 font-semibold">Indrakeeladri Security Grid</span>
            </div>

            <h2 className="font-['Outfit',sans-serif] text-[20px] sm:text-[23px] font-extrabold text-slate-900 dark:text-white leading-tight tracking-wide">
              Real-Time Crowd Telemetry &amp; Optical Intelligence
            </h2>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-3">
              {/* Feature 1 */}
              <div className="p-2.5 rounded-[6px] bg-white dark:bg-[#141924] border border-slate-200 dark:border-[#222b3d] shadow-xs transition-colors">
                <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 font-bold text-[11px] font-mono uppercase mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <span>AI Optical Vision</span>
                </div>
                <p className="text-[10.5px] text-slate-500 dark:text-slate-400 leading-tight">
                  CCTV optical nodes with real-time density &amp; anomaly detection
                </p>
              </div>

              {/* Feature 2 */}
              <div className="p-2.5 rounded-[6px] bg-white dark:bg-[#141924] border border-slate-200 dark:border-[#222b3d] shadow-xs transition-colors">
                <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-bold text-[11px] font-mono uppercase mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>Queue Telemetry</span>
                </div>
                <p className="text-[10.5px] text-slate-500 dark:text-slate-400 leading-tight">
                  5-channel live flow matrix, wait times &amp; S-22 holding release control
                </p>
              </div>

              {/* Feature 3 */}
              <div className="p-2.5 rounded-[6px] bg-white dark:bg-[#141924] border border-slate-200 dark:border-[#222b3d] shadow-xs transition-colors">
                <div className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400 font-bold text-[11px] font-mono uppercase mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                  <span>30-Sector Grid</span>
                </div>
                <p className="text-[10.5px] text-slate-500 dark:text-slate-400 leading-tight">
                  Police deployment readiness &amp; dual egress dispersal coordination
                </p>
              </div>
            </div>
          </div>

          {/* Map Graphic with Theme-Adaptive Background */}
          <div className="relative w-full flex-1 flex flex-col justify-center overflow-hidden bg-slate-100/90 dark:bg-[#0e131d] border border-slate-200 dark:border-[#222c3d] rounded-[8px] p-2 mt-2.5 shadow-inner transition-colors">
            <svg className="w-full h-full min-h-[380px] sm:min-h-[440px]" viewBox="0 0 840 520" preserveAspectRatio="xMidYMid meet">
              <defs>
                {/* Gradients for flow networks */}
                <linearGradient id="gradFeeder" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2563EB" />
                  <stop offset="50%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#60A5FA" />
                </linearGradient>

                <linearGradient id="gradHolding" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#C084FC" />
                </linearGradient>

                <linearGradient id="gradExit1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#06B6D4" />
                  <stop offset="100%" stopColor="#0284C7" />
                </linearGradient>

                <linearGradient id="gradExit2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F43F5E" />
                  <stop offset="100%" stopColor="#BE123C" />
                </linearGradient>
              </defs>

              {/* Theme-Adaptive Map Base Plate */}
              <rect width="100%" height="100%" fill={isDark ? '#0c1017' : '#f8fafc'} rx="6" opacity={isDark ? '0.95' : '1'} />

              {/* ---------------- 0. CONNECTED SECONDARY ROADS & ARTERIAL NETWORK ---------------- */}
              <g fill="none" stroke={isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(100, 116, 139, 0.28)'} strokeLinecap="round" strokeLinejoin="round">
                {/* 1. Prakasam Barrage / Krishna River Bund Outer Highway */}
                <path d="M 20,490 Q 200,470 380,480 T 680,495 T 820,500" strokeWidth="2.5" />

                {/* 2. Canal Road / Brahmin Street Highway */}
                <path d="M 20,410 Q 140,430 250,450 T 420,470 T 600,475 T 820,485" strokeWidth="1.8" />

                {/* 3. Arjun Street Feeder & Hill Foot Connector */}
                <path d="M 40,300 Q 120,290 200,320 T 310,340 T 420,380" strokeWidth="1.5" />

                {/* 4. Kummaripalem West Boundary Road */}
                <path d="M 70,160 L 70,260 L 80,350 L 120,480" strokeWidth="1.6" />

                {/* 5. Indrakeeladri South Hill Slope Perimeter Road */}
                <path d="M 120,480 Q 260,450 360,430 T 540,430 T 720,460 T 820,470" strokeWidth="1.5" />

                {/* 6. North Hill Ridge Ring Road */}
                <path d="M 70,160 Q 180,120 270,100 T 390,70 T 500,60 T 680,60 T 780,90 T 820,160" strokeWidth="1.5" />

                {/* 7. Mid-Mountain Traverse Highway */}
                <path d="M 190,280 Q 240,210 290,160 T 380,140 T 500,120 T 600,90 T 680,60" strokeWidth="1.4" />

                {/* 8. Central Cross-Valley Mountain Link */}
                <path d="M 360,240 Q 390,300 440,340 T 540,360 T 640,400 T 720,460" strokeWidth="1.4" />

                {/* 9. East Hillcrest Bypass */}
                <path d="M 680,60 Q 740,110 760,180 T 740,270 T 780,350 T 820,420" strokeWidth="1.4" />

                {/* 10. Hill Base Inter-Arterial Cross Links */}
                <path d="M 190,280 L 200,320 L 220,410 L 250,480" strokeWidth="1.2" />
                <path d="M 360,430 L 380,480" strokeWidth="1.2" />
                <path d="M 540,430 L 560,475" strokeWidth="1.2" />
                <path d="M 680,495 L 720,460" strokeWidth="1.2" />
              </g>

              {/* ---------------- 1. INGRESS & FEEDER ROAD CORRIDORS ---------------- */}
              {/* Route A: Kummaripalem Toll Gate Ingress (S-4) */}
              <path
                d="M 80,350 Q 130,320 190,280 T 280,250 T 360,240"
                fill="none"
                stroke="url(#gradFeeder)"
                strokeWidth="3.5"
                strokeLinecap="round"
              />

              {/* Route B: Vinayaka Temple Ghat Road Footpath (S-4 to S-6) */}
              <path
                d="M 130,440 Q 150,360 190,280"
                fill="none"
                stroke="url(#gradFeeder)"
                strokeWidth="2.5"
                strokeDasharray="5 3"
                opacity="0.85"
              />

              {/* Route C: Bhavani Ghat & Krishna River Feeder Canal Line */}
              <path
                d="M 50,260 Q 120,250 190,280"
                fill="none"
                stroke={isDark ? '#38BDF8' : '#0284C7'}
                strokeWidth="1.5"
                strokeDasharray="3 3"
                opacity="0.75"
              />

              {/* Route D: Police & Emergency Evacuation Bypass Corridor */}
              <path
                d="M 80,350 Q 160,420 230,400 T 340,360 T 460,420"
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                strokeDasharray="6 4"
                opacity="0.8"
              />

              {/* ---------------- 2. HOLDING AREA S-22 COMPLEX CORRIDORS ---------------- */}
              {/* S-22 Spur Line from Ghat Road */}
              <path
                d="M 230,260 Q 250,180 270,140 T 330,110 T 360,240"
                fill="none"
                stroke="url(#gradHolding)"
                strokeWidth="2.5"
                strokeDasharray="4 3"
              />
              {/* S-22 Holding buffer boxes */}
              <rect x="252" y="125" width="36" height="30" rx="3" fill={isDark ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.15)'} stroke={isDark ? '#A78BFA' : '#7C3AED'} strokeWidth="1.2" />
              <rect x="312" y="95" width="36" height="30" rx="3" fill={isDark ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.15)'} stroke={isDark ? '#A78BFA' : '#7C3AED'} strokeWidth="1.2" />
              <text x="270" y="142" textAnchor="middle" fill={isDark ? '#DDD6FE' : '#6D28D9'} fontSize="7.5" fontFamily="monospace" fontWeight="bold">CELL-A</text>
              <text x="330" y="112" textAnchor="middle" fill={isDark ? '#DDD6FE' : '#6D28D9'} fontSize="7.5" fontFamily="monospace" fontWeight="bold">CELL-B</text>

              {/* ---------------- 3. S-7 OM TURNING & 5-QUEUE LINES (S-8) ---------------- */}
              {/* S-7 OM Turning central hub loop */}
              <circle cx="360" cy="240" r="14" fill={isDark ? 'rgba(59, 130, 246, 0.25)' : 'rgba(37, 99, 235, 0.15)'} stroke={isDark ? '#60A5FA' : '#2563EB'} strokeWidth="1.5" />
              <text x="360" y="244" textAnchor="middle" fill={isDark ? '#93C5FD' : '#1D4ED8'} fontSize="8" fontFamily="monospace" fontWeight="bold">OM</text>

              {/* 5 Parallel Distinct Queue Channels (S-8 Corridor) */}
              {/* Line 1: General Q1 */}
              <path d="M 370,230 Q 410,205 450,180 T 560,145 T 650,105" fill="none" stroke="#22C55E" strokeWidth="2.2" strokeLinecap="round" />
              {/* Line 2: General Q2 */}
              <path d="M 372,235 Q 412,210 452,185 T 562,150 T 650,110" fill="none" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" />
              {/* Line 3: General Q3 */}
              <path d="M 374,240 Q 414,215 454,190 T 564,155 T 650,115" fill="none" stroke="#EAB308" strokeWidth="2.2" strokeLinecap="round" />
              {/* Line 4: Special ₹100 Line */}
              <path d="M 376,245 Q 420,230 470,225 T 570,165 T 650,120" fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeDasharray="5 2" />
              {/* Line 5: VIP / ₹300-₹500 Protocol Line */}
              <path d="M 378,250 Q 430,245 490,240 T 580,175 T 650,125" fill="none" stroke="#8B5CF6" strokeWidth="2.5" strokeDasharray="4 3" />

              {/* ---------------- 4. SANCTUM GARBHAGRIHA & ANTHARALAYAM BEACON ---------------- */}
              <g transform="translate(650, 110)">
                <circle r="26" fill="rgba(251, 191, 36, 0.22)" stroke="#FBBF24" strokeWidth="2" />
                <circle r="36" fill="none" stroke="#FBBF24" strokeWidth="1" strokeDasharray="3 3" className="animate-spin" style={{ transformOrigin: '0 0', animationDuration: '22s' }} />
                <circle r="7" fill="#FBBF24" />
              </g>

              {/* ---------------- 5. EXIT 1: KANAKA DURGA NAGAR DISPERSAL (S-10 TO S-15) ---------------- */}
              {/* Main Egress Spine */}
              <path
                d="M 650,110 Q 710,160 690,230 T 630,300 T 590,340 T 460,420"
                fill="none"
                stroke="url(#gradExit1)"
                strokeWidth="4"
                strokeLinecap="round"
              />
              {/* S-12 / S-13 Saree Counter & Mahamandapam Branch */}
              <path
                d="M 690,230 Q 730,270 700,320 T 590,340"
                fill="none"
                stroke="#38BDF8"
                strokeWidth="2"
                strokeDasharray="4 3"
                opacity="0.9"
              />
              {/* S-16 Annadanam Corridor Branch */}
              <path
                d="M 590,340 Q 640,390 570,430 T 460,420"
                fill="none"
                stroke="#0284C7"
                strokeWidth="2"
                strokeDasharray="4 4"
                opacity="0.9"
              />

              {/* ---------------- 6. EXIT 2: AVADHUTHA MOUNA MUNI DIRECT RAMP EGRESS ---------------- */}
              <path
                d="M 650,110 Q 700,95 740,90 T 800,120"
                fill="none"
                stroke="url(#gradExit2)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeDasharray="6 3"
              />

              {/* ---------------- 7. CCTV CAMERA MARKERS & RADAR NODES ---------------- */}
              {MAP_CAMERAS.map(cam => {
                const nodeColor =
                  cam.type === 'sanctum'
                    ? '#FBBF24'
                    : cam.type === 'holding'
                    ? '#C084FC'
                    : cam.type === 'exit'
                    ? '#38BDF8'
                    : cam.type === 'emergency'
                    ? '#10B981'
                    : '#3B82F6';

                return (
                  <g
                    key={cam.id}
                    transform={`translate(${cam.x}, ${cam.y})`}
                  >
                    {/* Pulsing Radar Ring */}
                    <circle r="13" fill="none" stroke={nodeColor} strokeWidth="1.2" opacity="0.6" className="animate-ping" style={{ transformOrigin: '0 0' }} />
                    <circle r="8" fill={isDark ? '#131B27' : '#FFFFFF'} stroke={nodeColor} strokeWidth="1.8" />

                    {/* Camera Dot */}
                    <circle r="3.4" fill={nodeColor} />

                    {/* Camera ID Tag */}
                    <rect
                      x="-19"
                      y="10"
                      width="38"
                      height="13"
                      rx="2"
                      fill={isDark ? '#151E2B' : '#FFFFFF'}
                      stroke={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)'}
                      strokeWidth="0.8"
                    />
                    <text x="0" y="19.5" textAnchor="middle" fill={isDark ? '#FFFFFF' : '#0F172A'} fontSize="7.5" fontFamily="monospace" fontWeight="bold">
                      {cam.id}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* ================= RIGHT SIDE (5 COLS): CREDENTIALS SECTION ================= */}
        <div className="lg:col-span-5 flex flex-col justify-center lg:pl-8 xl:pl-10">
          <div className="w-full flex flex-col justify-between py-2">
            <div>
              {/* Devasthanam Logo & Title Branding */}
              <div className="flex flex-col items-center text-center pb-5 mb-5 border-b border-slate-200 dark:border-[#20283a]">
                {/* Devasthanam Logo */}
                <div className="relative mb-3 group">
                  <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-500/30 via-indigo-500/30 to-blue-500/30 rounded-full blur-md opacity-80 group-hover:opacity-100 transition duration-500" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full p-1.5 bg-white dark:bg-[#141924] border-2 border-blue-500/50 shadow-md flex items-center justify-center backdrop-blur-md">
                    <img
                      src="/logo.png"
                      alt="Sri Durga Malleswara Swamyvarla Devasthanam Logo"
                      className="w-full h-full object-contain rounded-full drop-shadow-xs"
                    />
                  </div>
                </div>

                {/* Header Titles */}
                <h1 className="font-['Outfit',sans-serif] text-[19px] sm:text-[21px] uppercase tracking-[0.06em] font-extrabold text-slate-900 dark:text-white leading-tight">
                  Integrated Command – Dasara Navaratri 2026
                </h1>
                <p className="text-[12px] font-mono text-blue-600 dark:text-blue-400 font-semibold tracking-wider mt-1 uppercase">
                  NTR District Police · Indrakeeladri Security Grid
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-sans mt-0.5">
                  శ్రీ దుర్గా మల్లేశ్వర స్వామివార్ల దేవస్థానం · విజయవాడ
                </p>
              </div>

              {/* Subtitle Prompt */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-[15px] font-['Outfit',sans-serif] font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Operator Sign In
                  </h3>
                  <span className="font-mono text-[9px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30 uppercase font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    Grid Secure
                  </span>
                </div>
                <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                  Enter authorized Operator ID and security PIN to access live control operations.
                </p>
              </div>

              {/* Error Alert */}
              {error && (
                <div className="mb-4 p-3 rounded-[4px] bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-500/40 text-rose-700 dark:text-rose-300 text-[12.5px] flex items-center gap-2.5 animate-in fade-in slide-in-from-top-1 duration-150">
                  <AlertCircle className="w-4 h-4 text-rose-500 dark:text-rose-400 shrink-0" />
                  <span className="font-medium">{error}</span>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-3.5">
                {/* Username Input */}
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1 font-semibold">
                    Operator Username / ID
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      placeholder="Enter operator username"
                      autoFocus
                      autoComplete="username"
                      className="w-full bg-white dark:bg-[#121722] border border-slate-300 dark:border-[#263147] focus:border-blue-500 dark:focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-[4px] pl-10 pr-3.5 py-2.5 text-[13.5px] font-mono text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 outline-none transition"
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1 font-semibold">
                    Security Passcode / PIN
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter security passcode PIN"
                      autoComplete="current-password"
                      className="w-full bg-white dark:bg-[#121722] border border-slate-300 dark:border-[#263147] focus:border-blue-500 dark:focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-[4px] pl-10 pr-11 py-2.5 text-[13.5px] font-mono text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition cursor-pointer"
                      title={showPassword ? 'Hide passcode' : 'Show passcode'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Session Notice */}
                <div className="flex items-center justify-between pt-0.5 text-[11px] font-mono text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    <span>Protected Session: <strong className="text-slate-700 dark:text-slate-200">45 Mins</strong></span>
                  </span>
                  <span className="text-slate-400 dark:text-slate-500">SSL Encrypted</span>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full mt-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] text-white font-['Outfit',sans-serif] uppercase tracking-[0.08em] text-[13.5px] font-extrabold py-2.5 rounded-[4px] shadow-md shadow-blue-500/20 transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Authenticating Credentials...</span>
                    </>
                  ) : (
                    <>
                      <span>Authorize &amp; Access Terminal</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Card Footer */}
            <div className="mt-5 pt-3.5 border-t border-slate-200 dark:border-[#20283a] flex items-center justify-between text-[11px] font-mono text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                <span className="text-slate-700 dark:text-slate-300">Indrakeeladri Security Operations</span>
              </span>
              <span className="text-slate-400 dark:text-slate-500">v2.4</span>
            </div>

            {/* Bottom Security Disclaimer */}
            <p className="text-[11.5px] font-mono text-slate-500 dark:text-slate-400 text-center mt-3">
              Authorized Police &amp; Devasthanam Administration Personnel Only.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};
